import electronicFormService from "@/views/electronicForm/electronicFormService";
import { apiService, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { NotifyOptionsSuccess, NotifyOptionsError } from "@modernary/mui/components/notifications";
import { apiHosts } from "@/api/constants/apiHosts";

import type {
  ElectronicForm,
  ElectronicFormNew,
  ElectronicFormQuery,
  ElectronicFormBE,
  ElectronicFormSubmissionData,
  ElectronicFormValidationData,
  ESignRequest,
} from "./electronicForm";
import type { AxiosResponse } from "axios";
import { LE } from "@/constants/enums";

const URL = "electronic-forms";
const ENTITY_NAME = "Electronic Form";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<ElectronicFormBE>(apiHosts.AQMS, axiosParams);

const getDownloadLinkForElectronicForm = (id: string | number | undefined) => {
  if (!id) return "";
  return import.meta.env.VITE_AQMS_API_BASE_URL + URL + "/" + id + "/download";
};

const getElectronicForms = (params: ElectronicFormQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

const getElectronicFormById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.get(URL + "/" + id, {
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

type ElectronicFormNewValues = {
  entityId?: string | number;
  entityType: string;
  statusMurn: LE.ElectronicFormStatus;
  groupMurn: LE.ElectronicFormGroup;
  typeMurn: string;
};
const getNewElectronicForm = async <
  T extends ElectronicFormSubmissionData = ElectronicFormSubmissionData,
  V extends ElectronicFormValidationData = ElectronicFormValidationData,
>(
  values: ElectronicFormNewValues,
) => {
  const defaultApp = {
    data: {
      ...electronicFormService.getInitialData(),
      ...values,
    },
  } as AxiosResponse<ElectronicFormNew<T, V>, any>;

  return defaultApp;
};

const createElectronicForm = (electronicForm: ElectronicFormBE, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.post(URL, electronicForm, {
    successNotifCb,
    errorNotifCb,
  });
};

const updateElectronicForm = (electronicForm: ElectronicFormBE, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.put(URL + "/" + electronicForm.id, electronicForm, {
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {ElectronicForm} electronicForm This cannot be a 'ref' to an electronicForm
 */
const saveElectronicForm = (electronicForm: ElectronicFormBE, config = {}) => {
  if (electronicForm.id) {
    return updateElectronicForm(electronicForm, config);
  } else {
    return createElectronicForm(electronicForm, config);
  }
};

const deleteElectronicFormById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const eSignElectronicForm = (eSignRequest: ESignRequest, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.title = "Error Submitting Electronic Form";
    options.hideStatusCodes = [403];
    config?.errorNotifCb?.(options);
  };

  return api.post(URL + "/" + eSignRequest.id + "/esign", eSignRequest, {
    successNotifCb,
    errorNotifCb,
  });
};

export {
  getDownloadLinkForElectronicForm,
  deleteElectronicFormById,
  getElectronicForms,
  getElectronicFormById,
  getNewElectronicForm,
  createElectronicForm,
  updateElectronicForm,
  saveElectronicForm,
  eSignElectronicForm,
};
